'use client';
import React, { useRef, useEffect, useState } from 'react';

interface Pillar {
  id: string;
  number: string;
  title: string;
  body: string;
  stat: string;
  statLabel: string;
}

const pillars: Pillar[] = [
  {
    id: 'heritage',
    number: '01',
    title: 'Preserving Living Heritage',
    body: 'Every event we host is a thread in the tapestry of India\u2019s cultural memory. We work with master artists, classical institutions, and royal families to ensure centuries-old traditions reach new generations.',
    stat: '340+',
    statLabel: 'Heritage performances archived',
  },
  {
    id: 'artists',
    number: '02',
    title: 'Elevating the Artist',
    body: 'We believe the artist is the soul of civilisation. EventSphere provides fair compensation, global visibility, and institutional backing so that talent is never lost to obscurity.',
    stat: '1,200+',
    statLabel: 'Artists supported annually',
  },
  {
    id: 'community',
    number: '03',
    title: 'Rooting Culture in Community',
    body: 'From intimate haveli concerts to grand durbar gatherings, we bring culture back to the neighbourhood — making heritage accessible to every stratum of society.',
    stat: '80+',
    statLabel: 'Cities across India',
  },
  {
    id: 'future',
    number: '04',
    title: 'Investing in Tomorrow',
    body: 'A portion of every ticket sold funds scholarships for young classical musicians, restoration of heritage venues, and documentation of endangered art forms.',
    stat: '₹2.4 Cr',
    statLabel: 'Reinvested into the arts',
  },
];

function CountUp({ target, suffix = '' }: { target: string; suffix?: string }) {
  return (
    <span style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>
      {target}
      {suffix}
    </span>
  );
}

export default function ServingWithPurposeSection() {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 relative overflow-hidden" style={{ background: '#FAF7F2' }}>
      {/* Subtle ornamental watermark */}
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 w-96 h-96 opacity-[0.04] pointer-events-none"
        style={{
          background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)',
        }}
      />

      {/* Gold top border */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C 40%, transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-5">
            <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: '#C9A84C', fontFamily: 'Roboto, sans-serif' }}>
              Our Commitment
            </p>
            <h2
              className="text-4xl md:text-5xl leading-tight"
              style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#1A0A00' }}
            >
              Serving with
              <br />
              <em style={{ color: '#C9A84C', fontStyle: 'italic' }}>Purpose</em>
            </h2>
            <div className="mt-5 w-16 h-px" style={{ background: 'linear-gradient(90deg, #C9A84C, transparent)' }} />
          </div>
          <div className="md:col-span-7 md:pb-2">
            <p className="text-base leading-relaxed" style={{ color: '#5A4A35', fontFamily: 'Roboto, sans-serif', maxWidth: '520px' }}>
              EventSphere was founded not merely as a marketplace, but as a custodian of India's living cultural legacy. Every decision we make is guided by a singular purpose — to serve the arts, the artists, and the audiences who keep heritage alive.
            </p>
          </div>
        </div>

        {/* Pillars — asymmetric layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {/* Pillar 01 — large, spans 5 cols */}
          <div
            className="md:col-span-5 relative p-8 flex flex-col justify-between"
            style={{
              background: '#1A0A00',
              borderRadius: '16px',
              minHeight: '320px',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.1s, transform 0.6s ease 0.1s',
            }}
          >
            {/* Ornamental corner */}
            <div className="absolute top-5 right-5 w-8 h-8 opacity-30" style={{ border: '1px solid #C9A84C', borderRadius: '2px', transform: 'rotate(45deg)' }} />
            <div>
              <span className="text-xs tracking-widest" style={{ color: 'rgba(201,168,76,0.5)', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[0].number}
              </span>
              <h3 className="text-2xl mt-3 mb-4" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#FAF7F2' }}>
                {pillars[0].title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#A89070', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[0].body}
              </p>
            </div>
            <div className="mt-8 pt-6" style={{ borderTop: '1px solid rgba(201,168,76,0.2)' }}>
              <p className="text-3xl font-light" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>
                {pillars[0].stat}
              </p>
              <p className="text-xs mt-1" style={{ color: '#6B5A3E', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[0].statLabel}
              </p>
            </div>
          </div>

          {/* Right column — stacked 02 + 03 */}
          <div className="md:col-span-7 flex flex-col gap-5">
            {/* Pillar 02 */}
            <div
              className="relative p-7 flex gap-6 items-start"
              style={{
                background: '#FFFFFF',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: '16px',
                flex: 1,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease 0.2s, transform 0.6s ease 0.2s',
              }}
            >
              <div className="flex-shrink-0">
                <span className="text-xs tracking-widest block mb-2" style={{ color: 'rgba(201,168,76,0.6)', fontFamily: 'Roboto, sans-serif' }}>
                  {pillars[1].number}
                </span>
                <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 3C12 3 5 7 5 13C5 16.31 8.13 19 12 19C15.87 19 19 16.31 19 13C19 7 12 3 12 3Z" stroke="#C9A84C" strokeWidth="1.2" strokeLinejoin="round" />
                    <path d="M9 13L11 15L15 11" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#1A0A00' }}>
                  {pillars[1].title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#5A4A35', fontFamily: 'Roboto, sans-serif' }}>
                  {pillars[1].body}
                </p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-2xl" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>
                    {pillars[1].stat}
                  </span>
                  <span className="text-xs" style={{ color: '#8B7355', fontFamily: 'Roboto, sans-serif' }}>
                    {pillars[1].statLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Pillar 03 */}
            <div
              className="relative p-7 flex gap-6 items-start"
              style={{
                background: '#FFFFFF',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: '16px',
                flex: 1,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(24px)',
                transition: 'opacity 0.6s ease 0.3s, transform 0.6s ease 0.3s',
              }}
            >
              <div className="flex-shrink-0">
                <span className="text-xs tracking-widest block mb-2" style={{ color: 'rgba(201,168,76,0.6)', fontFamily: 'Roboto, sans-serif' }}>
                  {pillars[2].number}
                </span>
                <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="9" stroke="#C9A84C" strokeWidth="1.2" />
                    <path d="M12 7V12L15 15" stroke="#C9A84C" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-xl mb-2" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#1A0A00' }}>
                  {pillars[2].title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: '#5A4A35', fontFamily: 'Roboto, sans-serif' }}>
                  {pillars[2].body}
                </p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="text-2xl" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>
                    {pillars[2].stat}
                  </span>
                  <span className="text-xs" style={{ color: '#8B7355', fontFamily: 'Roboto, sans-serif' }}>
                    {pillars[2].statLabel}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Pillar 04 — full width bottom strip */}
          <div
            className="md:col-span-12 relative p-7 flex flex-col md:flex-row md:items-center gap-6"
            style={{
              background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(139,105,20,0.08) 100%)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: '16px',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(24px)',
              transition: 'opacity 0.6s ease 0.4s, transform 0.6s ease 0.4s',
            }}
          >
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="text-xs tracking-widest" style={{ color: 'rgba(201,168,76,0.6)', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[3].number}
              </span>
              <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.3)' }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L15 9H22L16.5 13.5L18.5 21L12 17L5.5 21L7.5 13.5L2 9H9L12 2Z" stroke="#C9A84C" strokeWidth="1.2" strokeLinejoin="round" />
                </svg>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl mb-1" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#1A0A00' }}>
                {pillars[3].title}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: '#5A4A35', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[3].body}
              </p>
            </div>
            <div className="flex-shrink-0 text-center md:text-right">
              <p className="text-3xl" style={{ fontFamily: '"Playfair Display", Georgia, serif', color: '#C9A84C' }}>
                {pillars[3].stat}
              </p>
              <p className="text-xs mt-1" style={{ color: '#8B7355', fontFamily: 'Roboto, sans-serif' }}>
                {pillars[3].statLabel}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Gold bottom border */}
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C 40%, transparent)' }} />
    </section>
  );
}
